import React, { useState, useEffect } from 'react';
import type { W9FormData } from '../types';

interface W9QuestionnaireProps {
  bearerToken: string;
  language?: string;
  prefillData?: Partial<W9FormData>;
  onSubmit: () => void;
  onSuccess: (result: any) => void;
  onError: (error: Error) => void;
  onProgress: (step: number, total: number) => void;
  environment?: 'production' | 'sandbox';
  debug?: boolean;
}

export const W9Questionnaire: React.FC<W9QuestionnaireProps> = ({
  bearerToken,
  prefillData,
  onSubmit,
  onSuccess,
  onError,
  onProgress,
  debug = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState<Partial<W9FormData>>({
    fullName: '',
    businessName: '',
    taxClassification: 'individual',
    otherClassification: '',
    payeeExemptCode: '',
    fatcaExemptCode: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    tin: '',
    signatureName: '',
    esignConsent: false,
    ...prefillData,
  });

  const totalSteps = 5;

  useEffect(() => {
    onProgress(currentStep, totalSteps);
  }, [currentStep, onProgress, totalSteps]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.fullName?.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (
        formData.taxClassification &&
        ['c-corp', 's-corp', 'partnership', 'llc-c', 'llc-s', 'llc-p'].includes(
          formData.taxClassification
        ) &&
        !formData.businessName?.trim()
      ) {
        newErrors.businessName = 'Business name is required for this classification';
      }
    } else if (step === 2) {
      if (!formData.taxClassification) {
        newErrors.taxClassification = 'Tax classification is required';
      }
      if (formData.taxClassification === 'other' && !formData.otherClassification?.trim()) {
        newErrors.otherClassification = 'Please specify your tax classification';
      }
    } else if (step === 3) {
      if (!formData.address?.trim()) {
        newErrors.address = 'Address is required';
      }
      if (!formData.city?.trim()) {
        newErrors.city = 'City is required';
      }
      if (!formData.state?.trim()) {
        newErrors.state = 'State is required';
      }
      if (!formData.zipCode?.trim()) {
        newErrors.zipCode = 'ZIP code is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
        newErrors.zipCode = 'Invalid ZIP code format';
      }
    } else if (step === 4) {
      if (!formData.tin?.trim()) {
        newErrors.tin = 'TIN is required';
      } else {
        const tinDigits = formData.tin.replace(/[^0-9]/g, '');
        if (tinDigits.length !== 9) {
          newErrors.tin = 'TIN must be 9 digits';
        }
      }
    } else if (step === 5) {
      if (!formData.signatureName?.trim()) {
        newErrors.signatureName = 'Signature name is required';
      }
      if (!formData.esignConsent) {
        newErrors.esignConsent = 'You must consent to electronic signature';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (field: keyof W9FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      return;
    }

    setIsSubmitting(true);
    onSubmit();

    try {
      const response = await fetch('/api/submissions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({
          form_type: 'w9',
          form_data: {
            ...formData,
            userAgent: navigator.userAgent,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Submission failed');
      }

      const result = await response.json();
      onSuccess(result);
    } catch (error) {
      console.error('W-9 submission error:', error);
      onError(error instanceof Error ? error : new Error('Unknown error occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="w9-step">
            <h3 className="w9-step-title">Step 1: Name and Business Information</h3>
            <p className="w9-step-description">
              Please provide your name exactly as shown on your income tax return.
            </p>

            <div className="w9-form-group">
              <label className="w9-label" htmlFor="fullName">
                Full Name <span className="w9-required">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                className={`w9-input ${errors.fullName ? 'w9-input-error' : ''}`}
                value={formData.fullName || ''}
                onChange={(e) => handleChange('fullName', e.target.value)}
                placeholder="Enter your full legal name"
              />
              {errors.fullName && <span className="w9-error-message">{errors.fullName}</span>}
              <p className="w9-field-hint">
                For individuals: First name, middle initial, last name
              </p>
            </div>

            <div className="w9-form-group">
              <label className="w9-label" htmlFor="businessName">
                Business Name (if different from above)
              </label>
              <input
                id="businessName"
                type="text"
                className={`w9-input ${errors.businessName ? 'w9-input-error' : ''}`}
                value={formData.businessName || ''}
                onChange={(e) => handleChange('businessName', e.target.value)}
                placeholder="Enter business name (optional)"
              />
              {errors.businessName && (
                <span className="w9-error-message">{errors.businessName}</span>
              )}
              <p className="w9-field-hint">
                If you operate under a business or "doing business as" (DBA) name
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="w9-step">
            <h3 className="w9-step-title">Step 2: Federal Tax Classification</h3>
            <p className="w9-step-description">
              Check the appropriate box for your federal tax classification.
            </p>

            <div className="w9-form-group">
              <div className="w9-radio-group">
                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="individual"
                    checked={formData.taxClassification === 'individual'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Individual/Sole proprietor or single-member LLC</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="c-corp"
                    checked={formData.taxClassification === 'c-corp'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>C Corporation</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="s-corp"
                    checked={formData.taxClassification === 's-corp'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>S Corporation</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="partnership"
                    checked={formData.taxClassification === 'partnership'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Partnership</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="trust-estate"
                    checked={formData.taxClassification === 'trust-estate'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Trust/Estate</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="llc-c"
                    checked={formData.taxClassification === 'llc-c'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Limited Liability Company (taxed as C Corporation)</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="llc-s"
                    checked={formData.taxClassification === 'llc-s'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Limited Liability Company (taxed as S Corporation)</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="llc-p"
                    checked={formData.taxClassification === 'llc-p'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Limited Liability Company (taxed as Partnership)</span>
                </label>

                <label className="w9-radio-label">
                  <input
                    type="radio"
                    name="taxClassification"
                    value="other"
                    checked={formData.taxClassification === 'other'}
                    onChange={(e) => handleChange('taxClassification', e.target.value)}
                  />
                  <span>Other (see instructions)</span>
                </label>
              </div>

              {errors.taxClassification && (
                <span className="w9-error-message">{errors.taxClassification}</span>
              )}
            </div>

            {formData.taxClassification === 'other' && (
              <div className="w9-form-group">
                <label className="w9-label" htmlFor="otherClassification">
                  Specify Classification <span className="w9-required">*</span>
                </label>
                <input
                  id="otherClassification"
                  type="text"
                  className={`w9-input ${errors.otherClassification ? 'w9-input-error' : ''}`}
                  value={formData.otherClassification || ''}
                  onChange={(e) => handleChange('otherClassification', e.target.value)}
                  placeholder="Enter your tax classification"
                />
                {errors.otherClassification && (
                  <span className="w9-error-message">{errors.otherClassification}</span>
                )}
              </div>
            )}

            <div className="w9-form-row">
              <div className="w9-form-group w9-form-group-half">
                <label className="w9-label" htmlFor="payeeExemptCode">
                  Exempt Payee Code (if any)
                </label>
                <input
                  id="payeeExemptCode"
                  type="text"
                  className="w9-input"
                  value={formData.payeeExemptCode || ''}
                  onChange={(e) => handleChange('payeeExemptCode', e.target.value)}
                  placeholder="Optional"
                  maxLength={2}
                />
              </div>

              <div className="w9-form-group w9-form-group-half">
                <label className="w9-label" htmlFor="fatcaExemptCode">
                  FATCA Exemption Code (if any)
                </label>
                <input
                  id="fatcaExemptCode"
                  type="text"
                  className="w9-input"
                  value={formData.fatcaExemptCode || ''}
                  onChange={(e) => handleChange('fatcaExemptCode', e.target.value)}
                  placeholder="Optional"
                  maxLength={1}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="w9-step">
            <h3 className="w9-step-title">Step 3: Address Information</h3>
            <p className="w9-step-description">
              Provide your current street address (P.O. boxes are not acceptable).
            </p>

            <div className="w9-form-group">
              <label className="w9-label" htmlFor="address">
                Street Address <span className="w9-required">*</span>
              </label>
              <input
                id="address"
                type="text"
                className={`w9-input ${errors.address ? 'w9-input-error' : ''}`}
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street"
              />
              {errors.address && <span className="w9-error-message">{errors.address}</span>}
            </div>

            <div className="w9-form-row">
              <div className="w9-form-group w9-form-group-half">
                <label className="w9-label" htmlFor="city">
                  City <span className="w9-required">*</span>
                </label>
                <input
                  id="city"
                  type="text"
                  className={`w9-input ${errors.city ? 'w9-input-error' : ''}`}
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                />
                {errors.city && <span className="w9-error-message">{errors.city}</span>}
              </div>

              <div className="w9-form-group w9-form-group-quarter">
                <label className="w9-label" htmlFor="state">
                  State <span className="w9-required">*</span>
                </label>
                <input
                  id="state"
                  type="text"
                  className={`w9-input ${errors.state ? 'w9-input-error' : ''}`}
                  value={formData.state || ''}
                  onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                  placeholder="CA"
                  maxLength={2}
                />
                {errors.state && <span className="w9-error-message">{errors.state}</span>}
              </div>

              <div className="w9-form-group w9-form-group-quarter">
                <label className="w9-label" htmlFor="zipCode">
                  ZIP Code <span className="w9-required">*</span>
                </label>
                <input
                  id="zipCode"
                  type="text"
                  className={`w9-input ${errors.zipCode ? 'w9-input-error' : ''}`}
                  value={formData.zipCode || ''}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                  placeholder="12345"
                />
                {errors.zipCode && <span className="w9-error-message">{errors.zipCode}</span>}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="w9-step">
            <h3 className="w9-step-title">Step 4: Taxpayer Identification Number</h3>
            <p className="w9-step-description">
              Enter your Social Security Number (SSN) or Employer Identification Number (EIN).
            </p>

            <div className="w9-form-group">
              <label className="w9-label" htmlFor="tin">
                SSN or EIN <span className="w9-required">*</span>
              </label>
              <input
                id="tin"
                type="text"
                className={`w9-input ${errors.tin ? 'w9-input-error' : ''}`}
                value={formData.tin || ''}
                onChange={(e) => {
                  // Format as XXX-XX-XXXX or XX-XXXXXXX
                  let value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 9) {
                    if (value.length > 5) {
                      value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
                    } else if (value.length > 3) {
                      value = value.slice(0, 3) + '-' + value.slice(3);
                    }
                    handleChange('tin', value);
                  }
                }}
                placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                maxLength={11}
              />
              {errors.tin && <span className="w9-error-message">{errors.tin}</span>}
              <p className="w9-field-hint">
                <strong>Security Note:</strong> Your TIN will be encrypted and securely stored. We
                use industry-standard AES-256 encryption.
              </p>
            </div>

            <div className="w9-info-box">
              <h4>Why do we need your TIN?</h4>
              <p>
                The IRS requires us to obtain your Taxpayer Identification Number to properly
                report payments made to you. This information is used for tax reporting purposes
                only and is protected by strict security measures.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="w9-step">
            <h3 className="w9-step-title">Step 5: Certification and Electronic Signature</h3>
            <p className="w9-step-description">
              Please review the certifications and provide your electronic signature.
            </p>

            <div className="w9-certification-box">
              <h4>Certification</h4>
              <p>Under penalties of perjury, I certify that:</p>
              <ol>
                <li>
                  The number shown on this form is my correct taxpayer identification number (or I
                  am waiting for a number to be issued to me); and
                </li>
                <li>
                  I am not subject to backup withholding because: (a) I am exempt from backup
                  withholding, or (b) I have not been notified by the Internal Revenue Service
                  (IRS) that I am subject to backup withholding as a result of a failure to report
                  all interest or dividends, or (c) the IRS has notified me that I am no longer
                  subject to backup withholding; and
                </li>
                <li>I am a U.S. citizen or other U.S. person; and</li>
                <li>
                  The FATCA code(s) entered on this form (if any) indicating that I am exempt from
                  FATCA reporting is correct.
                </li>
              </ol>
            </div>

            <div className="w9-form-group">
              <label className="w9-label" htmlFor="signatureName">
                Electronic Signature <span className="w9-required">*</span>
              </label>
              <input
                id="signatureName"
                type="text"
                className={`w9-input w9-signature-input ${errors.signatureName ? 'w9-input-error' : ''}`}
                value={formData.signatureName || ''}
                onChange={(e) => handleChange('signatureName', e.target.value)}
                placeholder="Type your full legal name"
              />
              {errors.signatureName && (
                <span className="w9-error-message">{errors.signatureName}</span>
              )}
              <p className="w9-field-hint">
                By typing your name, you are providing a legally binding electronic signature.
              </p>
            </div>

            <div className="w9-form-group">
              <label className="w9-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.esignConsent || false}
                  onChange={(e) => handleChange('esignConsent', e.target.checked)}
                />
                <span>
                  I consent to using electronic signatures and records <span className="w9-required">*</span>
                </span>
              </label>
              {errors.esignConsent && (
                <span className="w9-error-message">{errors.esignConsent}</span>
              )}
            </div>

            <div className="w9-info-box">
              <h4>Electronic Signature Disclosure</h4>
              <p>
                By checking the box above and typing your name, you acknowledge and agree to the
                use of electronic signatures as provided in the Electronic Signatures in Global and
                National Commerce Act (ESIGN Act). Your electronic signature has the same legal
                effect as a handwritten signature.
              </p>
              <p className="w9-signature-date">
                <strong>Date:</strong> {new Date().toLocaleDateString('en-US')}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w9-questionnaire-container">
      <div className="w9-form-header">
        <h2 className="w9-form-title">Form W-9</h2>
        <p className="w9-form-subtitle">
          Request for Taxpayer Identification Number and Certification
        </p>
      </div>

      <div className="w9-form-content">{renderStep()}</div>

      <div className="w9-form-actions">
        {currentStep > 1 && (
          <button
            type="button"
            className="w9-button w9-button-secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="w9-button w9-button-primary"
          onClick={handleNext}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : currentStep === totalSteps ? 'Submit' : 'Next'}
        </button>
      </div>

      {debug && (
        <div className="w9-debug-info">
          <h4>Debug Information</h4>
          <pre>{JSON.stringify({ currentStep, formData, errors }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default W9Questionnaire;
